import { useState } from "react";
import { X, Eye, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { fetchAuthOpts } from "@/lib/api";

/**
 * Modal "Ficha Cliente": muestra los datos de impresión del cliente,
 * permite añadir un comentario libre y descargar/imprimir un PDF A4 horizontal.
 *
 * No modifica los datos del cliente. El PDF se genera en el backend
 * (GET /api/customers/{id}/card-pdf?comments=...).
 */
export default function CustomerCardModal({ customer, onClose }) {
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const buildUrl = () => {
    const params = new URLSearchParams();
    if (comments.trim()) params.set("comments", comments.trim());
    return `${api.defaults.baseURL}/customers/${customer.id}/card-pdf${params.toString() ? "?" + params : ""}`;
  };

  const fetchPdf = async () => {
    const r = await fetch(buildUrl(), fetchAuthOpts());
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.blob();
  };

  const preview = async () => {
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Liberar la URL pasados unos segundos (la pestaña ya cargó el blob)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(`No se pudo generar la vista previa (${err.message})`);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ficha-cliente-${customer.code || customer.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("PDF descargado");
    } catch (err) {
      toast.error(`Error descargando PDF (${err.message})`);
    } finally {
      setBusy(false);
    }
  };

  const printPdf = async () => {
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      // Iframe oculto para lanzar print() directamente
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch (_e) { window.open(url, "_blank", "noopener,noreferrer"); }
        setTimeout(() => {
          iframe.remove();
          URL.revokeObjectURL(url);
        }, 60000);
      };
    } catch (err) {
      toast.error(`Error imprimiendo (${err.message})`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-3xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-card-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0] sticky top-0 bg-white">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              // {customer.code || "—"}
            </div>
            <div className="font-display font-bold text-base">Ficha Cliente</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]" data-testid="card-close" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Resumen de impresión */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Datos personales">
              <Row label="Nombre / Razón Social" value={customer.name} />
              <Row label="Teléfono" value={customer.phone} />
            </Section>
            <Section title="Dirección">
              <Row label="Dirección" value={customer.address} />
              <Row label="Código postal" value={customer.postal_code} />
              <Row label="Población" value={customer.city} />
              <Row label="Provincia" value={customer.province} />
              <Row label="País" value={customer.country} />
            </Section>
          </div>

          {/* Comentarios para impresión */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Comentarios para impresión
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Comentarios que aparecerán en el PDF impreso. No modifican los datos del cliente."
              data-testid="card-comments"
              className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            />
            <div className="text-[10px] text-[#525252] mt-1">
              {comments.length} / 2000 caracteres
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="px-5 py-3 border-t border-[#E5E5E0] flex flex-wrap gap-2 justify-end bg-[#FAFAF8] sticky bottom-0">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="card-cancel"
            className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm disabled:opacity-50"
          >
            Cerrar
          </button>
          <button
            onClick={preview}
            disabled={busy}
            data-testid="card-preview"
            className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
          >
            <Eye size={14} /> Vista previa
          </button>
          <button
            onClick={download}
            disabled={busy}
            data-testid="card-download"
            className="inline-flex items-center gap-2 border border-[#6E8B3D] text-[#6E8B3D] hover:bg-[#6E8B3D] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
          >
            <Download size={14} /> Descargar PDF
          </button>
          <button
            onClick={printPdf}
            disabled={busy}
            data-testid="card-print"
            className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Printer size={14} /> Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#FAFAF8] border border-[#E5E5E0] p-4">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D] mb-2">
        // {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="text-sm">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block">
        {label}
      </span>
      <span className="text-[#111110]">{value || <span className="text-[#525252]">—</span>}</span>
    </div>
  );
}
